import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Icon,
  V2Badge,
  V2Card,
  V2CardHeader,
  V2DataGrid,
  V2EmptyState,
  V2Tabs,
  LoadingState,
  StatePanel,
  type V2GridColumn,
} from '@sakhtar/design-system/react';
import type { ApiGetState } from '../../lib/useApiGet.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import type {
  BankAccountListResponse,
  TreasuryAccount,
  TreasuryAccountListResponse,
  TreasuryTransaction,
  TreasuryTransactionListResponse,
} from '../../lib/types/finance.js';
import { SETTLEMENT_METHOD_LABEL } from '../../lib/types/finance.js';
import type { AmountDisplayUnit } from '@sakhtar/money';
import {
  amountColumnHeader,
  formatRialFa,
  journalHref,
  treasuryTxnStatusLabel,
} from '../../lib/finance-display.js';
import { PartyLabel } from './PartyLabel.js';

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html — /t/kimia/finance, تب «خزانه»
 *   (frame `finTreasury`): card «حساب‌های بانکی و صندوق» + card «گردش اخیر
 *   خزانه» (تاریخ · حساب · ورودی · خروجی · سند · وضعیت) with the footer note
 *   that bank payment only counts after journal approval. The kind tabs
 *   (همه / دریافت / پرداخت) follow the Finance.dc treasury board.
 * canonical pattern: V2CardHeader card list + V2DataGrid with tabs in `actions`.
 * status: PARTIAL_REAL — everything shown is real API data:
 *   - accounts: GET .../finance/treasury/accounts (apps/api/src/finance/
 *     treasury.controller.ts `listAccounts`) + bank name from
 *     GET .../treasury/bank-accounts.
 *   - activity: GET .../treasury/transactions (`listTransactions`).
 *   The reference's per-account BALANCE and its share bar are NOT shown:
 *   `serializeTreasuryAccount` (subledger-serialize.ts:50-60) returns no
 *   balance field and the treasury controller has no balance endpoint. Instead
 *   each account shows «خالص گردش قطعی» = Σ posted receipts − Σ posted payments
 *   for that account (BigInt-accumulated), labelled as such — never as a
 *   ledger balance. The «انتقال» tab, «شروع تطبیق بانکی» action and the
 *   reference's «شرح» column are omitted: there is no transfer txn kind
 *   (`kind` is receipt|payment only), no bank-reconciliation endpoint, and
 *   `serializeTreasuryTransactionSummary` (subledger-serialize.ts:79-95) does
 *   not return a description. These are listed as backend gaps.
 * state reference: ready · loading · empty · permission_denied · network_error
 */

type KindTab = 'all' | 'receipt' | 'payment';

const KIND_TABS: readonly { readonly id: KindTab; readonly label: string }[] = [
  { id: 'all', label: 'همه' },
  { id: 'receipt', label: 'دریافت' },
  { id: 'payment', label: 'پرداخت' },
];

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'همهٔ وضعیت‌ها' },
  { value: 'draft', label: 'پیش‌نویس' },
  { value: 'posted', label: 'ثبت‌شده' },
  { value: 'reversed', label: 'برگشت‌خورده' },
] as const;

const TXN_STATUS_TONE: Record<string, 'neutral' | 'success' | 'warn'> = {
  draft: 'neutral',
  posted: 'success',
  reversed: 'warn',
};

interface TxnRow extends TreasuryTransaction {
  readonly id: string;
}

/** Posted-only net flow per treasury account, BigInt-accumulated (AGENTS.md §8). */
function postedNetFlowByAccount(txns: readonly TreasuryTransaction[]): ReadonlyMap<string, bigint> {
  const net = new Map<string, bigint>();
  for (const t of txns) {
    if (t.status !== 'posted') continue;
    const prev = net.get(t.treasuryAccountId) ?? 0n;
    if (t.kind === 'receipt') net.set(t.treasuryAccountId, prev + BigInt(t.amountRial));
    else if (t.kind === 'payment') net.set(t.treasuryAccountId, prev - BigInt(t.amountRial));
  }
  return net;
}

export interface TreasuryOverviewProps {
  readonly base: string;
  readonly slug: string;
  readonly unit: AmountDisplayUnit;
  readonly accounts: ApiGetState<TreasuryAccountListResponse>;
  readonly txns: ApiGetState<TreasuryTransactionListResponse>;
  readonly bankAccounts: ApiGetState<BankAccountListResponse>;
}

export function TreasuryOverview({
  base,
  slug,
  unit,
  accounts,
  txns,
  bankAccounts,
}: TreasuryOverviewProps) {
  const [kindTab, setKindTab] = useState<KindTab>('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState('');

  const accountList = accounts.status === 'ready' ? accounts.data.items : null;
  const txnList = txns.status === 'ready' ? txns.data.items : null;

  const accountNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of accountList ?? []) map.set(a.treasuryAccountId, a.name);
    return map;
  }, [accountList]);

  const bankNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (bankAccounts.status === 'ready') {
      for (const b of bankAccounts.data.items) if (b.bankName) map.set(b.bankAccountId, b.bankName);
    }
    return map;
  }, [bankAccounts]);

  const netByAccount = useMemo(() => postedNetFlowByAccount(txnList ?? []), [txnList]);

  const rows: TxnRow[] = useMemo(() => {
    return [...(txnList ?? [])]
      .filter((t) => kindTab === 'all' || t.kind === kindTab)
      .filter((t) => !statusFilter || t.status === statusFilter)
      .filter((t) => !accountFilter || t.treasuryAccountId === accountFilter)
      .sort((a, b) => b.businessDate.localeCompare(a.businessDate))
      .map((t) => ({ ...t, id: t.treasuryTransactionId }));
  }, [txnList, kindTab, statusFilter, accountFilter]);

  // Totals row: posted rows only, BigInt-accumulated, over the visible filter.
  const postedTotals = useMemo(() => {
    let inflow = 0n;
    let outflow = 0n;
    for (const t of rows) {
      if (t.status !== 'posted') continue;
      if (t.kind === 'receipt') inflow += BigInt(t.amountRial);
      else if (t.kind === 'payment') outflow += BigInt(t.amountRial);
    }
    return { inflow, outflow };
  }, [rows]);

  const columns: readonly V2GridColumn<TxnRow>[] = [
    {
      key: 'date',
      header: 'تاریخ',
      render: (row) => <span className="sk-num">{displayJalaliDate(row.businessDate)}</span>,
    },
    {
      key: 'party',
      header: 'طرف‌حساب',
      render: (row) =>
        row.partyId ? (
          <PartyLabel slug={slug} partyId={row.partyId} />
        ) : (
          <span style={{ color: 'var(--sk-fg-muted)' }}>—</span>
        ),
    },
    {
      key: 'account',
      header: 'حساب',
      render: (row) => accountNameById.get(row.treasuryAccountId) ?? '—',
    },
    {
      key: 'inflow',
      header: amountColumnHeader('ورودی', unit),
      align: 'end',
      footer:
        postedTotals.inflow > 0n ? (
          <span className="sk-num">{formatRialFa(postedTotals.inflow.toString(), unit)}</span>
        ) : undefined,
      render: (row) =>
        row.kind === 'receipt' ? (
          <span className="sk-num" style={{ fontWeight: 600, color: 'var(--sk-debit)' }}>
            {formatRialFa(row.amountRial, unit)}
          </span>
        ) : null,
    },
    {
      key: 'outflow',
      header: amountColumnHeader('خروجی', unit),
      align: 'end',
      footer:
        postedTotals.outflow > 0n ? (
          <span className="sk-num">{formatRialFa(postedTotals.outflow.toString(), unit)}</span>
        ) : undefined,
      render: (row) =>
        row.kind === 'payment' ? (
          <span className="sk-num" style={{ fontWeight: 600, color: 'var(--sk-credit)' }}>
            {formatRialFa(row.amountRial, unit)}
          </span>
        ) : null,
    },
    {
      key: 'method',
      header: 'روش تسویه',
      render: (row) =>
        row.settlementMethod ? (
          <span>
            {SETTLEMENT_METHOD_LABEL[row.settlementMethod]}
            {row.bankReference ? (
              <span className="ltr sk-num" style={{ color: 'var(--sk-fg-muted)' }}>
                {' '}
                · {row.bankReference}
              </span>
            ) : null}
          </span>
        ) : (
          <span style={{ color: 'var(--sk-fg-muted)' }}>—</span>
        ),
    },
    {
      key: 'ref',
      header: 'شمارهٔ منبع',
      render: (row) => (
        <span className="ltr sk-num" style={{ fontFamily: 'var(--sk-font-mono)' }}>
          {row.sourceReference}
        </span>
      ),
    },
    {
      key: 'journal',
      header: 'سند',
      render: (row) => (
        <Link
          to={journalHref(base, row.originatingJournalId)}
          className="ltr sk-num font-mono"
          style={{ fontWeight: 600, color: 'var(--sk-brand-600, #2563eb)' }}
        >
          {row.journalNumber
            ? row.journalNumber.startsWith('JRN')
              ? row.journalNumber
              : `JRN-${row.journalNumber}`
            : 'سند حسابداری'}
        </Link>
      ),
    },
    {
      key: 'description',
      header: 'شرح',
      render: (row) => <span>{row.description || row.sourceReference || '—'}</span>,
    },
    {
      key: 'status',
      header: 'وضعیت',
      render: (row) => (
        <V2Badge tone={TXN_STATUS_TONE[row.status] ?? 'neutral'}>
          {treasuryTxnStatusLabel(row.status)}
        </V2Badge>
      ),
    },
    {
      key: 'open',
      header: '',
      hideable: false,
      render: (row) => (
        <Link to={`${base}/finance/treasury/transactions/${row.treasuryTransactionId}`}>
          جزئیات
        </Link>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
      <div style={{ flex: '1 1 340px', minWidth: 0 }}>
        {accounts.status === 'loading' ? <LoadingState /> : null}
        {accounts.status === 'error' ? (
          <StatePanel
            variant={accounts.error.status === 403 ? 'permission_denied' : 'network_error'}
            title={
              accounts.error.status === 403 ? 'دسترسی ندارید' : 'حساب‌های خزانه قابل نمایش نیست'
            }
          />
        ) : null}
        {accountList ? (
          <V2Card>
            <V2CardHeader
              icon="building"
              title="حساب‌های بانکی و صندوق"
              subtitle={`${accountList.length.toLocaleString('fa-IR')} حساب`}
            />
            {accountList.length === 0 ? (
              <V2EmptyState icon="info" title="حساب خزانه‌ای ثبت نشده" />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {accountList.map((a: TreasuryAccount) => {
                  const net = netByAccount.get(a.treasuryAccountId) ?? 0n;
                  const bankName = a.bankAccountId ? bankNameById.get(a.bankAccountId) : undefined;
                  const selected = accountFilter === a.treasuryAccountId;
                  return (
                    <button
                      key={a.treasuryAccountId}
                      type="button"
                      aria-pressed={selected}
                      title="نمایش گردش این حساب"
                      onClick={() => setAccountFilter(selected ? '' : a.treasuryAccountId)}
                      style={{
                        all: 'unset',
                        boxSizing: 'border-box',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '11px 14px',
                        borderBottom: '1px solid var(--sk-divider)',
                        background: selected ? 'var(--sk-selected-bg, transparent)' : undefined,
                      }}
                    >
                      <span
                        style={{
                          width: 34,
                          height: 34,
                          flex: '0 0 34px',
                          borderRadius: 10,
                          display: 'grid',
                          placeContent: 'center',
                          color: 'var(--sk-brand-600)',
                          background: 'color-mix(in oklch, var(--sk-brand-500), transparent 88%)',
                        }}
                      >
                        <Icon name={a.kind === 'bank' ? 'building' : 'finance'} size="md" />
                      </span>
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                        }}
                      >
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            justifyContent: 'space-between',
                            gap: 8,
                          }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--sk-fg)' }}>
                            {a.name}
                          </span>
                          <span
                            className="sk-num"
                            title="خالص گردش قطعی (دریافت‌ها منهای پرداخت‌های ثبت‌شده)"
                            style={{ fontSize: 14, fontWeight: 800, color: 'var(--sk-fg)' }}
                          >
                            {txns.status === 'ready' ? formatRialFa(net.toString(), unit) : '—'}
                          </span>
                        </span>
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8,
                            fontSize: 11.5,
                            color: 'var(--sk-fg-muted)',
                          }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span
                              className="ltr sk-num"
                              style={{ fontFamily: 'var(--sk-font-mono)' }}
                            >
                              {a.code}
                            </span>
                            <V2Badge tone="info">{a.kindLabel}</V2Badge>
                            {bankName ? <span>{bankName}</span> : null}
                          </span>
                          <V2Badge tone={a.status === 'active' ? 'success' : 'neutral'}>
                            {a.status === 'active' ? 'فعال' : 'غیرفعال'}
                          </V2Badge>
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            <div
              style={{
                fontSize: 12.5,
                lineHeight: 1.8,
                color: 'var(--sk-fg-muted)',
                padding: '9px 14px',
                borderTop: '1px dashed var(--sk-divider)',
              }}
            >
              مبلغ هر حساب «خالص گردش قطعی» است؛ دریافت‌های ثبت‌شده منهای پرداخت‌های ثبت‌شدهٔ همان
              حساب. مانده‌ی دفتری حساب از این API در دسترس نیست.
            </div>
          </V2Card>
        ) : null}
      </div>

      <div style={{ flex: '2 1 560px', minWidth: 0 }}>
        {txns.status === 'loading' ? <LoadingState /> : null}
        {txns.status === 'error' ? (
          <StatePanel
            variant={txns.error.status === 403 ? 'permission_denied' : 'network_error'}
            title={txns.error.status === 403 ? 'دسترسی ندارید' : 'گردش خزانه قابل نمایش نیست'}
          />
        ) : null}
        {txnList ? (
          <V2DataGrid
            icon="trend"
            title="گردش اخیر خزانه"
            subtitle={`${rows.length.toLocaleString('fa-IR')} تراکنش`}
            actions={
              <V2Tabs
                items={KIND_TABS}
                activeId={kindTab}
                onChange={(id) => setKindTab(id as KindTab)}
              />
            }
            densitySwitch={false}
            filters={[
              {
                id: 'status',
                label: 'وضعیت',
                value: statusFilter,
                options: STATUS_FILTER_OPTIONS,
                onChange: setStatusFilter,
              },
              {
                id: 'account',
                label: 'حساب',
                value: accountFilter,
                options: [
                  { value: '', label: 'همهٔ حساب‌ها' },
                  ...(accountList ?? []).map((a) => ({
                    value: a.treasuryAccountId,
                    label: a.name,
                  })),
                ],
                onChange: setAccountFilter,
              },
            ]}
            columns={columns}
            rows={rows}
            rowKey={(r) => r.id}
            rowTone={(r) =>
              r.status === 'posted' ? 'success' : r.status === 'reversed' ? 'warn' : 'neutral'
            }
            footerLabel="جمع تراکنش‌های ثبت‌شده"
            zebra
            legendNote="پرداخت از بانک فقط بعد از تأیید سند انجام می‌شود؛ تراکنش‌های «پیش‌نویس» هنوز در گردش قطعی حساب‌ها و جمع‌ها نیامده‌اند."
            emptyState={<V2EmptyState icon="info" title="گردشی ثبت نشده" />}
          />
        ) : null}
      </div>
    </div>
  );
}
